import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../../store';
import axios from '../../../axios';
import { ToastAnt } from '../../Shared/Toast/ToastAnt';
import { PropertyType } from '../../../shared/models/propertyType.enum';
import { FailureMechanismTemplate } from '../FailureMechanismTemplate/failureMechanismTemplate.slice';
import { FailureModeCategoryTemplate } from '../FailureModeCategoryTemplate/failureModeCategoryTemplate.slice';
import { EightDTemplate } from '../EightDTemplate/eightDTemplate.slice';

enum IconType {
  Rectangle = 0,
  Circle = 1,
  Polygon = 2
}

export type FindingTypeTemplateDetails = {
  id: string;
  name: string;
  version: string;
  description: string;
  icon?: IconType;
  color: string;
  properties: Array<FindingTypeProperty>;
  isMLRelated: boolean;
};

export type FailureModeCategory = {
  id: string;
  failureModeCategoryTemplate: FailureModeCategoryTemplate;
};

export type EightD = {
  id: string;
  eightDTemplate: EightDTemplate;
};

export type FindingTypeDetails = {
  id?: string;
  description?: string;
  findingTypeTemplateId: string;
  findingTypeTemplate?: FindingTypeTemplateDetails;
  rules: Array<FindingTypeRuleDetails>;
  representations: Array<FindingTypePropertyRepresentation>;
  order: number | 0;
  failureMechanismTemplateIds: string[];
  failureMechanisms: FailureMechanism[];
  failureModeCategoryTemplateIds: string[];
  failureModeCategories: FailureModeCategory[];
  eightDTemplateIds: string[];
  eightDs: EightD[];
};

export type FailureMechanism = {
  id: string;
  failureMechanismTemplate: FailureMechanismTemplate;
};

export type AnnotationStandardDetails = DefineAnnotationStandard & { findingTypes: FindingTypeDetails[] } & { hasDefaultMaintenanceTrigger: boolean };

export type UpdateAnnotationStandardDetails = DefineAnnotationStandard & {
  findingTypesToAdd: FindingTypeDetails[];
  findingTypesToUpdate: FindingTypeDetails[];
  findingTypeIdsToRemove: string[];
  SharedUserGroups: UserGroup[];
};

export type DefineAnnotationStandard = {
  id?: string;
  name: string;
  standardBody: string;
  version: string;
  description: string;
  released: boolean;
  deprecated: boolean;
  parentAnnotationStandardId?: string;
  parentAnnotationStandardName?: string;
  parentAnnotationStandardVersion?: string;
};

export type FindingTypePropertyRepresentation = {
  id?: string;
  findingTypePropertyId: string;
  title: string;
  unit: string;
  group: string;
  isMandatory: boolean;
  show: boolean;
};

export type FindingTypeRuleDetails = {
  id?: string;
  findingTypeId?: string;
  findingTypeTemplateId: string;
  items: Array<FindingTypeRuleItemDetails>;
};

export type CreateFindingTypeRuleWithIndex = FindingTypeRuleDetails & { index: number; errorMark: boolean };

export type FindingTypeRuleItemDetails = {
  id?: string;
  findingTypeRuleId?: string;
  findingTypePropertyId: string;
  value: string;
};

export type FindingTypeProperty = {
  id: string;
  templateId: string;
  name: string;
  fullName: string;
  description: string;
  units: string;
  values: string;
  type: PropertyType;
};

export type TypeRepresentations = {
  representations: Array<FindingTypePropertyRepresentation>;
};

export type UserGroup = {
  userGroupId: string;
  name: string;
  isShared: boolean;
};

type State = {
  currentPage: number;
  totalNumberOfPages: number;
  annotationStandard: AnnotationStandardDetails;
  findingTypeTemplates: Array<FindingTypeTemplateDetails>;
  failureMechanismTemplates: Array<FailureMechanismTemplate>;
  failureModeCategoryTemplates: Array<FailureModeCategoryTemplate>;
  eightDTemplates: Array<EightDTemplate>;
  selectedFindingTypeTemplates: Array<FindingTypeTemplateDetails>;
  isLoading: boolean;
  standardIsLoading: boolean;
  done: boolean;
  findingTypesToDelete: string[];
  canEdit: boolean;
  usergroups: Array<UserGroup>;
  clonedUrl: string;
  isCloning: boolean;
};

let initialState: State = {
  currentPage: 0,
  totalNumberOfPages: 4,
  annotationStandard: {} as AnnotationStandardDetails,
  findingTypeTemplates: [],
  failureMechanismTemplates: [],
  failureModeCategoryTemplates: [],
  eightDTemplates: [],
  selectedFindingTypeTemplates: [],
  isLoading: false,
  standardIsLoading: true,
  done: false,
  findingTypesToDelete: [],
  canEdit: true,
  usergroups: [],
  clonedUrl: '',
  isCloning: false
};

const annotationStandardFormSlice = createSlice({
  name: 'annotationStandardCreation',
  initialState,
  reducers: {
    resetState() {
      return initialState;
    },
    setAnnotationStandard(state, action: PayloadAction<AnnotationStandardDetails>) {
      const annotationStandard = action.payload;
      if (annotationStandard.findingTypes.every(x => x.order === 0)) {
        annotationStandard.findingTypes.forEach((ft, index) => (ft.order = index));
      }
      annotationStandard.findingTypes = annotationStandard.findingTypes.sort((a, b) => a.order - b.order);

      state.annotationStandard = annotationStandard;
      state.selectedFindingTypeTemplates = annotationStandard.findingTypes.map(x => x.findingTypeTemplate!);
      state.canEdit = !annotationStandard.released && !annotationStandard.deprecated;
    },
    startAnnotationStandardLoad(state) {
      state.standardIsLoading = true;
    },
    finishAnnotationStandardLoad(state) {
      state.standardIsLoading = false;
    },
    setFindingTypeTemplates(state, action: PayloadAction<Array<FindingTypeTemplateDetails>>) {
      state.findingTypeTemplates = action.payload;
    },
    setFailureMechanismTemplates(state, action: PayloadAction<Array<FailureMechanismTemplate>>) {
      state.failureMechanismTemplates = action.payload;
    },
    setFailureModeCategoryTemplates(state, action: PayloadAction<Array<FailureModeCategoryTemplate>>) {
      state.failureModeCategoryTemplates = action.payload;
    },
    setEightDTemplates(state, action: PayloadAction<Array<EightDTemplate>>) {
      state.eightDTemplates = action.payload;
    },
    newTypeSelected(state, action: PayloadAction<FindingTypeTemplateDetails>) {
      const { id: findingTypeTemplateId } = action.payload;

      state.selectedFindingTypeTemplates.push(action.payload);

      const findingTypes = state.annotationStandard.findingTypes ?? [];

      state.annotationStandard.findingTypes = [
        ...findingTypes,
        {
          ...action.payload,
          findingTypeTemplateId,
          representations: [],
          rules: [],
          order: findingTypes.length,
          failureMechanismTemplateIds: [],
          failureMechanisms: [],
          failureModeCategoryTemplateIds: [],
          failureModeCategories: [],
          eightDTemplateIds: [],
          eightDs: []
        }
      ];
    },
    updateSelectedFindingTypeTemplates(state, action: PayloadAction<FindingTypeTemplateDetails[]>) {
      state.selectedFindingTypeTemplates = action.payload;
    },
    removeSelectedType(state, action: PayloadAction<string>) {
      const deleted = state.annotationStandard.findingTypes.find(x => x.findingTypeTemplateId === action.payload);

      if (deleted && deleted.id) {
        state.findingTypesToDelete.push(deleted.id);
      }

      state.selectedFindingTypeTemplates = state.selectedFindingTypeTemplates.filter(x => x.id !== action.payload);
      state.annotationStandard.findingTypes = state.annotationStandard.findingTypes
        .filter(x => x.findingTypeTemplateId !== action.payload)
        .sort((a, b) => a.order - b.order)
        .map((x, index) => ({
          ...x,
          order: index
        }));
    },
    moveToNextPage(state) {
      state.currentPage++;
    },
    moveToPreviousPage(state) {
      state.currentPage--;
    },
    populatedDefineData(state, action: PayloadAction<DefineAnnotationStandard>) {
      state.annotationStandard = {
        ...state.annotationStandard,
        ...action.payload,
        findingTypes: state.annotationStandard.findingTypes ? state.annotationStandard.findingTypes : []
      };
    },
    populateFindingsType(state, action: PayloadAction<Array<FindingTypeDetails>>) {
      state.annotationStandard.findingTypes = action.payload;
    },
    populateRules(state, action: PayloadAction<Array<CreateFindingTypeRuleWithIndex>>) {
      state.annotationStandard.findingTypes.forEach(type => {
        type.rules = action.payload
          .filter(x => x.findingTypeTemplateId === type.findingTypeTemplateId)
          .map(x => ({ findingTypeTemplateId: x.findingTypeTemplateId, items: x.items }));
      });
    },
    startCreating(state) {
      state.isLoading = true;
    },
    finishCreatingSuccessful(state) {
      state.isLoading = false;
      state.done = true;
    },
    finishCreatingFailed(state) {
      state.isLoading = false;
    },
    clearFinished(state) {
      state.done = false;
    },
    populateUserGroups(state, action: PayloadAction<Array<UserGroup>>) {
      state.usergroups = action.payload;
    },
    shareUserGroup(state, action: PayloadAction<string>) {
      state.usergroups.forEach(group => {
        if (group.userGroupId == action.payload) {
          group.isShared = true;
        }
      });
    },
    unshareUserGroup(state, action: PayloadAction<string>) {
      state.usergroups.forEach(group => {
        if (group.userGroupId == action.payload) {
          group.isShared = false;
        }
      });
    },
    setClonedUrl(state, action: PayloadAction<string>) {
      state.clonedUrl = action.payload;
    },
    setIsCloning(state, action: PayloadAction<boolean>) {
      state.isCloning = action.payload;
    }
  }
});

export const {
  resetState,
  setFindingTypeTemplates,
  newTypeSelected,
  removeSelectedType,
  moveToNextPage,
  moveToPreviousPage,
  populatedDefineData,
  populateFindingsType,
  populateRules,
  startCreating,
  finishCreatingFailed,
  finishCreatingSuccessful,
  setAnnotationStandard,
  startAnnotationStandardLoad,
  finishAnnotationStandardLoad,
  clearFinished,
  populateUserGroups,
  shareUserGroup,
  unshareUserGroup,
  setClonedUrl,
  setIsCloning,
  updateSelectedFindingTypeTemplates,
  setFailureMechanismTemplates,
  setFailureModeCategoryTemplates,
  setEightDTemplates
} = annotationStandardFormSlice.actions;

export default annotationStandardFormSlice.reducer;

export const getFindingTypeTemplates = (): AppThunk => async dispatch => {
  const response = await axios.get<Array<FindingTypeTemplateDetails>>('FindingTypeTemplates');
  dispatch(setFindingTypeTemplates(response.data));
};

export const getMLFindingTypeTemplates = (): AppThunk => async dispatch => {
  const response = await axios.get<Array<FindingTypeTemplateDetails>>('FindingTypeTemplates/ml');
  dispatch(setFindingTypeTemplates(response.data));
};

export const getFailureMechanismTemplates = (): AppThunk => async dispatch => {
  const response = await axios.get<Array<FailureMechanismTemplate>>('FailureMechanism');
  dispatch(setFailureMechanismTemplates(response.data));
};
export const getFailureModeCategoryTemplates = (): AppThunk => async dispatch => {
  const response = await axios.get<Array<FailureModeCategoryTemplate>>('FailureModeCategory');
  dispatch(setFailureModeCategoryTemplates(response.data));
};
export const getEightDTemplates = (): AppThunk => async dispatch => {
  const response = await axios.get<Array<EightDTemplate>>('EightD');
  dispatch(setEightDTemplates(response.data));
};
export const getFindingTypeProperties =
  (id: string): AppThunk =>
  async (dispatch, getState) => {
    const response = await axios.get(`FindingTypeTemplates/${id}`);
    const properties = response.data.properties;
    let selectedType = getState().annotationStandardForm.findingTypeTemplates.find(x => x.id === id);
    if (selectedType) {
      selectedType = { ...selectedType, properties };
      dispatch(newTypeSelected(selectedType));
    }
  };

export const loadAnnotationStandard =
  (id: string): AppThunk =>
  async (dispatch, getState) => {
    if (id) {
      dispatch(startAnnotationStandardLoad());
      const response = await axios.get<AnnotationStandardDetails>(`AnnotationStandards/${id}/true`);
      dispatch(setAnnotationStandard(response.data));
      const usergroups = await axios.get<Array<UserGroup>>(`AnnotationStandards/${id}/usergroups`);
      dispatch(populateUserGroups(usergroups.data));
    }

    dispatch(finishAnnotationStandardLoad());
  };

export const finishAnnotationStandardForm = (): AppThunk => async (dispatch, getState) => {
  if (getState().annotationStandardForm.annotationStandard.id) {
    dispatch(updateAnnotationStandard());
  } else {
    dispatch(createAnnotationStandard());
  }
};

export const createAnnotationStandard = (): AppThunk => async (dispatch, getState) => {
  const { annotationStandard } = getState().annotationStandardForm;

  try {
    dispatch(startCreating());
    const response = await axios.post(`AnnotationStandards`, annotationStandard);
    dispatch(finishCreatingSuccessful());
    ToastAnt('Added');
  } catch (error: any) {
    console.log(error);
    dispatch(finishCreatingFailed());
    ToastAnt(error?.response?.data);
  }
};

export const cloneAnnotationStandard = (): AppThunk => async (dispatch, getState) => {
  const { annotationStandard, usergroups } = getState().annotationStandardForm;

  let name, version, parentAnnotationStandardId;
  try {
    const data = await getNameVersionForClonedStandard(annotationStandard.id!);
    name = data.name;
    version = data.version;
    parentAnnotationStandardId = annotationStandard.id;
  } catch (error) {
    ToastAnt('An error occurred');
    dispatch(setIsCloning(false));
    return;
  }

  //can be send with all id's, create dto on be ignores it
  const cloned: AnnotationStandardDetails & { sharedUserGroups: Array<UserGroup> } = {
    ...annotationStandard,
    released: false,
    deprecated: false,
    name: name,
    version: version,
    parentAnnotationStandardId: parentAnnotationStandardId,
    sharedUserGroups: usergroups
  };

  try {
    dispatch(setIsCloning(true));
    const response = await axios.post<string>(`AnnotationStandards`, cloned);
    dispatch(setClonedUrl(`/annotation-manager/annotation-standards/${response.data}`));
    ToastAnt(`${annotationStandard.name} has been cloned`);
  } catch (error: any) {
    console.log(error);
    ToastAnt(error?.response?.data);
  }

  dispatch(setIsCloning(false));
};

export const updateAnnotationStandard = (): AppThunk => async (dispatch, getState) => {
  try {
    dispatch(startCreating());

    const { annotationStandard, findingTypesToDelete, usergroups } = getState().annotationStandardForm;

    const model: UpdateAnnotationStandardDetails = {
      ...annotationStandard,
      findingTypesToAdd: annotationStandard.findingTypes.filter(x => !x.id),
      findingTypesToUpdate: annotationStandard.findingTypes.filter(x => x.id),
      findingTypeIdsToRemove: findingTypesToDelete,
      SharedUserGroups: usergroups
    };

    const response = await axios.put(`AnnotationStandards`, model);
    dispatch(finishCreatingSuccessful());
    ToastAnt('Updated');
  } catch (error: any) {
    dispatch(finishCreatingFailed());
    ToastAnt(error?.response?.data);
  }
};

export const updateUserGroups =
  (id: string, shared: boolean): AppThunk =>
  dispatch => {
    if (shared) {
      dispatch(shareUserGroup(id));
    } else {
      dispatch(unshareUserGroup(id));
    }
  };

const getNameVersionForClonedStandard = async (id: string) => {
  const response = await axios.get<string>(`AnnotationStandards/nameversion-for-cloned/${id}`);
  const data = response.data.split('|');
  return { name: data[0], version: data[1] };
};
